import React, { useRef, useState } from "react"
import {
    View,
    StyleSheet,
    Text,
    TouchableOpacity,
    ScrollView,
} from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"

const CIRCLE_SIZE = 56
const CARD_GAP = 10
// Each card takes ~78% of the available viewport so the next card peeks ~22%
// on the right edge — signals "there is more" without forcing the user to discover scrolling.
const CARD_WIDTH_RATIO = 0.78
const DEFAULT_VIEWPORT = 420

/**
 * Snap-to-card horizontal timeline : N stations (WE1...WEn + Brevet).
 * Each card is ~78% viewport wide, with the next card peeking ~22% — natural "there's more" affordance.
 * Tap a card to expand its detail below. The per-card CTA ("Tapez pour le détail")
 * is sufficient affordance — no global pulse hint needed.
 *  stations = [{ id, weekLabel, subLabel, emoji, moduleShort, moduleDetails, isFinal }]
 *  viewportWidth = inner width of the parent frame (excluding its own padding)
 */
const BrevetWeekendTimeline = ({ stations, viewportWidth }) => {
    const vw = viewportWidth && viewportWidth > 0 ? viewportWidth : DEFAULT_VIEWPORT
    const CARD_WIDTH = Math.min(Math.round(vw * CARD_WIDTH_RATIO), 360)
    const SNAP_INTERVAL = CARD_WIDTH + CARD_GAP
    const [openIdx, setOpenIdx] = useState(null)
    const scrollRef = useRef(null)

    const onTapStation = (idx) => {
        setOpenIdx((prev) => (prev === idx ? null : idx))
        // Scroll horizontally so the tapped card aligns at the start of the viewport.
        scrollRef.current?.scrollTo({ x: idx * SNAP_INTERVAL, animated: true })
    }

    const active = openIdx !== null ? stations[openIdx] : null

    return (
        <View>
            <ScrollView
                ref={scrollRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                snapToInterval={SNAP_INTERVAL}
                decelerationRate="fast"
                snapToAlignment="start"
                contentContainerStyle={styles.timelineRow}
            >
                {stations.map((s, idx) => (
                    <TouchableOpacity
                        key={s.id}
                        onPress={s.isFinal ? undefined : () => onTapStation(idx)}
                        activeOpacity={s.isFinal ? 1 : 0.85}
                        disabled={s.isFinal}
                        style={[
                            styles.card,
                            { width: CARD_WIDTH, marginRight: idx < stations.length - 1 ? CARD_GAP : 0 },
                            openIdx === idx && styles.cardActive,
                            s.isFinal && styles.cardFinal,
                        ]}
                    >
                        <View style={styles.cardHeader}>
                            <View
                                style={[
                                    styles.circle,
                                    openIdx === idx && styles.circleActive,
                                    s.isFinal && styles.circleFinal,
                                ]}
                            >
                                {s.isFinal ? (
                                    <Text style={styles.circleFinalIcon}>🏁</Text>
                                ) : (
                                    <Text
                                        style={[
                                            styles.circleNum,
                                            openIdx === idx && styles.circleNumActive,
                                        ]}
                                    >
                                        {idx + 1}
                                    </Text>
                                )}
                            </View>
                            <View style={styles.cardHeaderText}>
                                <Text
                                    style={[
                                        styles.weekLabel,
                                        (openIdx === idx || s.isFinal) && styles.weekLabelActive,
                                    ]}
                                    numberOfLines={1}
                                >
                                    {s.weekLabel}
                                </Text>
                                {s.subLabel ? (
                                    <Text style={styles.weekDate} numberOfLines={1}>
                                        {s.subLabel}
                                    </Text>
                                ) : null}
                            </View>
                        </View>

                        {!s.isFinal ? (
                            <View style={styles.cardBody}>
                                <Text style={styles.stationEmoji}>{s.emoji}</Text>
                                <Text
                                    style={[
                                        styles.stationModule,
                                        openIdx === idx && styles.stationModuleActive,
                                    ]}
                                    numberOfLines={2}
                                >
                                    {s.moduleShort}
                                </Text>
                                <Text style={styles.cardCta} numberOfLines={1}>
                                    {openIdx === idx ? "Détail ouvert ↓" : "Tapez pour le détail"}
                                </Text>
                            </View>
                        ) : (
                            <View style={styles.cardBody}>
                                <Text style={styles.stationFinalLabel} numberOfLines={2}>
                                    Jour J
                                </Text>
                            </View>
                        )}
                    </TouchableOpacity>
                ))}
            </ScrollView>

            {active ? (
                <View style={styles.detail}>
                    <View style={styles.detailHeader}>
                        <Text style={styles.detailEmoji}>{active.emoji}</Text>
                        <View style={styles.detailHeaderText}>
                            <Text style={styles.detailWeek}>
                                {active.weekLabel}
                                {active.subLabel ? ` · ${active.subLabel}` : ""}
                            </Text>
                            <Text style={styles.detailModule}>
                                {active.moduleShort}
                            </Text>
                        </View>
                        <TouchableOpacity
                            onPress={() => setOpenIdx(null)}
                            style={styles.detailClose}
                            activeOpacity={0.7}
                        >
                            <FontAwesome5
                                name="times"
                                size={12}
                                color={theme.colors.grey}
                            />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.detailRow}>
                        <Text style={styles.detailRowIcon}>🎓</Text>
                        <Text style={styles.detailRowText}>
                            <Text style={styles.detailRowBold}>2 h de cours en visio avec votre tuteur</Text>
                            {" "}— petit groupe de 2 à 3 élèves max pour rester concentré.
                        </Text>
                    </View>

                    <View style={styles.detailRow}>
                        <Text style={styles.detailRowIcon}>✨</Text>
                        <Text style={styles.detailRowText}>
                            <Text style={styles.detailRowBold}>Bonus autonomie</Text>
                            {" "}— travail guidé par l'IA pédago + chat tuteur toute la semaine.
                        </Text>
                    </View>

                    {active.moduleDetails?.length > 0 ? (
                        <View style={styles.modulesBlock}>
                            <Text style={styles.modulesTitle}>Modules ciblés</Text>
                            {active.moduleDetails.map((m) => (
                                <View key={m.id} style={styles.moduleLine}>
                                    <Text style={styles.moduleBullet}>•</Text>
                                    <Text style={styles.moduleLabel}>{m.label}</Text>
                                </View>
                            ))}
                        </View>
                    ) : null}
                </View>
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create({
    timelineRow: {
        flexDirection: "row",
        alignItems: "stretch",
        paddingVertical: 6,
        paddingHorizontal: 4,
    },
    card: {
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
        paddingVertical: 14,
        paddingHorizontal: 14,
    },
    cardActive: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    cardFinal: {
        borderColor: theme.colors.success,
        backgroundColor: theme.colors.successLight,
        borderStyle: "dashed",
    },
    cardHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        marginBottom: 8,
    },
    cardHeaderText: {
        flex: 1,
    },
    cardBody: {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
    },
    cardCta: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.primary,
        marginTop: 6,
    },
    circle: {
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        borderRadius: CIRCLE_SIZE / 2,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
    },
    circleActive: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primary,
    },
    circleFinal: {
        borderColor: theme.colors.success,
        backgroundColor: theme.colors.white,
        borderStyle: "dashed",
    },
    circleNum: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.light,
    },
    circleNumActive: {
        color: theme.colors.white,
    },
    circleFinalIcon: {
        fontSize: 26,
    },
    weekLabel: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        color: theme.colors.grey,
    },
    weekLabelActive: {
        color: theme.colors.primary,
    },
    weekDate: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.light,
        marginTop: 2,
    },
    stationEmoji: {
        fontSize: 28,
        lineHeight: 32,
    },
    stationModule: {
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        color: theme.colors.grey,
        textAlign: "left",
    },
    stationModuleActive: {
        color: theme.colors.primary,
    },
    stationFinalLabel: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 16,
        color: theme.colors.success,
    },
    detail: {
        marginTop: 12,
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 12,
        padding: 14,
        gap: 10,
    },
    detailHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginBottom: 2,
    },
    detailEmoji: {
        fontSize: 24,
    },
    detailHeaderText: {
        flex: 1,
    },
    detailWeek: {
        fontFamily: "Montserrat-Bold",
        fontSize: 10,
        color: theme.colors.primary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    detailModule: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 14,
        color: theme.colors.primary,
    },
    detailClose: {
        width: 26,
        height: 26,
        alignItems: "center",
        justifyContent: "center",
    },
    detailRow: {
        flexDirection: "row",
        gap: 8,
        alignItems: "flex-start",
    },
    detailRowIcon: {
        fontSize: 16,
    },
    detailRowText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 17,
    },
    detailRowBold: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
    },
    modulesBlock: {
        marginTop: 4,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: theme.colors.white,
    },
    modulesTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 10,
        color: theme.colors.primary,
        marginBottom: 4,
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },
    moduleLine: {
        flexDirection: "row",
        gap: 6,
        marginBottom: 2,
    },
    moduleBullet: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
    },
    moduleLabel: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
    },
})

export default BrevetWeekendTimeline
